import { ColDef, RowClickedEvent, ColumnState } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { createSaveColumnState, defaultColDef, getDefaultRowIndexColumnConfig, getRowStyle, onGridReady } from "src/utils/grid/gridStyleUtils";

import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Select from "src/components/common/gridWrapper/Grid_Select";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { KSK1090_R01Dto, defaultKSK1090_R01Data, GridRowData } from "./Dto_KSK1090_R01";
import { useKSK1090_R01Store } from "./Store_KSK1090_R01";
import { formatDate } from "src/utils/string/stringUtil";

import { formatNumber } from "src/utils/number/numberUtils";
import { CommonCodeColumn } from "src/utils/grid/GridColumnContent";
import { cellDateComparator, onlyTouchGridStyle } from "src/utils/grid/gridFunctionUtil";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";

const COLUMN_STATE_KEY = 'KSK1090_R01-grid-column-state';

interface Props {
    gridRef: React.RefObject<AgGridReact<KSK1090_R01Dto> | null>;
}

export const Table_KSK1090_R01 = (props: Props) => {

    const { gridRef } = props;

    const { 
        KSK1090_R01List,
        selectedKSK1090_R01List,  addSelectedKSK1090_R01List, removeSelectedKSK1090_R01List, initSelectedKSK1090_R01List,
        
        columnState, setColumnState,
    } = useKSK1090_R01Store();

    const { 
        jobTeamList,
        ordTypeList,
        unitList,
        compCdList, 
        employeeCdList,
        workPlaceList,
    } = useCommonCodeStore();

    const codeMaps = useMemo(() => {
        return {
            ordType: new Map(ordTypeList.map(v => [v.minorCd, v.codeNm])),
            workPlace: new Map(workPlaceList.map(v => [v.workplaceCd, v.workplaceNm])),
            unit: new Map(unitList.map(v => [v.minorCd, v.codeNm])),
            emp: new Map(employeeCdList.map(v => [v.empNo, v.empNm])),
            comp: new Map(compCdList.map(v => [v.compCd, v.compNm])),
        };
    }, [ordTypeList, workPlaceList, unitList, employeeCdList, compCdList]);

    // 복합키 생성 함수
    const getCompositeKey = useCallback((row: GridRowData) => 
        `${row.transportNo}|${row.itemCd}|${row.customCd}|${row.warehouseCd}`,
    []);

    const {
        createRowClassRules,
        ensureSelectedRow,
    } = useGridSettingContent({
        gridRef,
        key: 'valueIndex',
        gridData: KSK1090_R01List || [],
        defaultGridData: defaultKSK1090_R01Data as GridRowData,
        selectedRow: selectedKSK1090_R01List,
    });
    const getRowIdForGrid = useCallback((params: any) => {
        const id = params.data?.valueIndex || params.id;
        return String(id);
    }, []);
    const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

    const headerNames: Record<string, string> = {
        rowIndex: '',
        compNm: '사업장명',
        logicCenterNm: '물류센터',
        warehouseNm: '창고코드',
        customCd: '거래처',
        customNm: '상호',
        transportDt: '입고일자',
        inoutClassNm: '입고구분',
        itemCd: '품목코드',
        itemNm: '품명',
        itemSpec: '규격',
        itemStatus: '품목등급',
        unit: '단위',
        qty: '수불량',
        cost: '수불원가',
        amt: '금액',
        
    };

    const getCellStyle = (params: any, key: string) => {
        return onlyTouchGridStyle(
            key,
            'compNm',
        )(params);
    }

    const baseColumnDefs: ColDef<KSK1090_R01Dto>[] = useMemo(() => {
        const result: ColDef<KSK1090_R01Dto>[] = [
            ...getDefaultRowIndexColumnConfig(),
            { headerName: headerNames.compNm, colId: 'compNm', field: 'compNm', width: 120, cellStyle: (p:any) => getCellStyle(p, 'compNm'), editable: false },
            { headerName: headerNames.logicCenterNm, colId: 'logicCenterNm', field: 'logicCenterNm', width: 120, cellStyle: (p:any) => getCellStyle(p, 'logicCenterNm'), editable: false },
            { headerName: headerNames.warehouseNm, colId: 'warehouseNm', field: 'warehouseNm', width: 120, cellStyle: (p:any) => getCellStyle(p, 'warehouseNm'), editable: false },
            { headerName: headerNames.customCd, colId: 'customCd', field: 'customCd', width: 110, cellStyle: (p:any) => getCellStyle(p, 'customCd'), editable: false },
            { headerName: headerNames.customNm, colId: 'customNm', field: 'customNm', width: 140, cellStyle: (p:any) => getCellStyle(p, 'customNm'), editable: false },
            { headerName: headerNames.transportDt, colId: 'transportDt', field: 'transportDt', width: 120, cellStyle: (p:any) => getCellStyle(p, 'transportDt'), editable: false, valueFormatter: (params: any) => {
                    if (params.data?.transportDt) { const date = new Date(params.data.transportDt); if (!isNaN(date.getTime())) { return formatDate(date); } } return '';
                }, comparator: cellDateComparator },
            { headerName: headerNames.inoutClassNm, colId: 'inoutClassNm', field: 'inoutClassNm', width: 100, cellStyle: (p:any) => getCellStyle(p, 'inoutClassNm'), editable: false },
            { headerName: headerNames.itemCd, colId: 'itemCd', field: 'itemCd', width: 110, cellStyle: (p:any) => getCellStyle(p, 'itemCd'), editable: false },
            { headerName: headerNames.itemNm, colId: 'itemNm', field: 'itemNm', width: 200, cellStyle: (p:any) => getCellStyle(p, 'itemNm'), editable: false },
            { headerName: headerNames.itemSpec, colId: 'itemSpec', field: 'itemSpec', width: 200, cellStyle: (p:any) => getCellStyle(p, 'itemSpec'), editable: false },
            { headerName: headerNames.itemStatus, colId: 'itemStatus', field: 'itemStatus', width: 90, cellStyle: (p:any) => getCellStyle(p, 'itemStatus'), editable: false },
            { headerName: headerNames.unit, colId: 'unit', field: 'unit', width: 80, cellStyle: (p:any) => getCellStyle(p, 'unit'), ...CommonCodeColumn.options(unitList, false, undefined, undefined) },
            { headerName: headerNames.qty, colId: 'qty', field: 'qty', width: 90, cellStyle: (p:any) => getCellStyle(p, 'qty'), editable: false, type: 'numericColumn', valueFormatter: (p:any) => formatNumber(p.value || 0) },
            { headerName: headerNames.cost, colId: 'cost', field: 'cost', width: 90, cellStyle: (p:any) => getCellStyle(p, 'cost'), editable: false, type: 'numericColumn', valueFormatter: (p:any) => formatNumber(p.value || 0) },
            { headerName: headerNames.amt, colId: 'amt', field: 'amt', width: 110, cellStyle: (p:any) => getCellStyle(p, 'amt'), editable: false, type: 'numericColumn', valueFormatter: (p:any) => formatNumber(p.value || 0) },
        ];
        
        return result;
    }, [hoveredRowIndex, KSK1090_R01List, selectedKSK1090_R01List, compCdList, ordTypeList, unitList, employeeCdList, workPlaceList, codeMaps]);

	const columnDefs: ColDef<KSK1090_R01Dto>[] = useMemo(() => {
		if (columnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<KSK1090_R01Dto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, baseColumnDefs]);

    const getRowStyleForPUR2020 = useCallback((params: any) => {
        if (params.node.rowPinned === 'bottom') {
            return { background: '#F0F0F0', fontWeight: 'bold', borderTop: 'none' };
        }
        return undefined;
    }, []);

    const handleRowClick = useCallback((e: RowClickedEvent<GridRowData>) => {
        if (e.node.rowPinned === 'bottom') return;
        const rowData = e.data;
        if (!rowData) return;
        
        const compositeKey = getCompositeKey(rowData);
        const isSelected = selectedKSK1090_R01List.includes(compositeKey);
        
        if (isSelected) {
            removeSelectedKSK1090_R01List([compositeKey]);
        } else {
            addSelectedKSK1090_R01List([compositeKey]);
        }
    }, [selectedKSK1090_R01List, addSelectedKSK1090_R01List, removeSelectedKSK1090_R01List, getCompositeKey]);

    const pinnedBottomRow = useMemo(() => {
        const detailRows = KSK1090_R01List;
        const totals = detailRows.reduce((acc, item) => {
            return {
                qty: acc.qty + (Number(item.qty) || 0),
                cost: acc.cost + (Number(item.cost) || 0),
                amt: acc.amt + (Number(item.amt) || 0),
                editable: false,
            };
        }, { qty: 0, cost: 0, amt: 0, editable: false });

        return [{
            rowIndex: '',
            compNm: detailRows.length.toLocaleString() + "건",
            qty: totals.qty,
            cost: totals.cost,
            amt: totals.amt,
            editable: false,
        }];
    }, [KSK1090_R01List]);

    useEffect(() => { ensureSelectedRow(); }, [KSK1090_R01List, ]);

    // 복합키 기반 rowClassRules
    const rowClassRules = useMemo(() => ({
        'selected': (params: any) => {
            if (!params.data) return false;
            const compositeKey = getCompositeKey(params.data);
            return selectedKSK1090_R01List.includes(compositeKey);
        }
    }), [selectedKSK1090_R01List, getCompositeKey]);

    const handleRowDragEnd = useCallback((e: any) => {
        const api = gridRef.current?.api || e?.api;
        if (!api) return;
        
        if (api.applyColumnState) {
            api.applyColumnState({ defaultState: { sort: null as any, sortIndex: null as any } });
        }
        
        const reordered: GridRowData[] = [];
        api.forEachNodeAfterFilterAndSort((node: any, idx: number) => {
            if (!node?.data) return;
            reordered.push({
                ...node.data,
                orderIndex: idx + 1,
                orderIndexSeq: 0,
            } as GridRowData);
        });
        // 드래그 후 리스트 자체는 유지 (필요시 상태 업데이트 로직 추가)
    }, [gridRef]);

    return (
        <div className="flex flex-col gap-3 h-full min-h-0">
            <GridContextWrapper<GridRowData>
                gridRef={gridRef}
                paddedGridData={KSK1090_R01List}
                initSelected={initSelectedKSK1090_R01List}
				defaultRowData={defaultKSK1090_R01Data}
                baseColumnDefs={columnDefs}
                setColumnState={setColumnState}
                localStorageKey={COLUMN_STATE_KEY}
                enableRowDrag={true}
                onAfterReorder={handleRowDragEnd}
            >
                <Grid_Select<GridRowData>
                    ref={gridRef}
                    rowData={KSK1090_R01List}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    getRowId={getRowIdForGrid}
					setHoveredRowIndex={setHoveredRowIndex}
					columnStateKey={COLUMN_STATE_KEY}
					setColumnState={setColumnState}
                    onGridReady={(params) => {
                        params.api.getDisplayedRowCount();
                        params.api.getRenderedNodes();
                    }}
                    onRowClicked={handleRowClick}
                    pinnedBottomRowData={pinnedBottomRow}
                    rowClassRules={rowClassRules}
                    getRowStyle={getRowStyleForPUR2020}
                    rowDragManaged={true}
                    rowDragEntireRow={true}
                    onRowDragEnd={handleRowDragEnd}
                    isRowSelectable={(node) => !!node.data}
                    suppressRowClickSelection={false}
                    ensureDomOrder={true}
                />
            </GridContextWrapper>
        </div>
    );
}


